import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../lib/session';
import { useHub, usePrivate } from '../context';
import {
  ARMOR_BUCKETS, capacity, CONSUMABLES, ENGRAMS, equipItem, inBucket, isArmor, isCrafted, isLocked, isMasterwork, isTenet, isWeapon,
  loadGear, loadStash, loadVendors, LOST_ITEMS, MATERIALS, MODIFICATIONS, moveItem, pullFromPostmaster, saleCount,
  VAULT, WEAPON_BUCKETS, type GearChar, type Stash, type StashItem, type Vendor,
} from '../character';
import { Art, Draw, ItemTile, ListRow, Message, Pic, SectionHead, SmallButton, Split } from '../ui';

import Guardian3D from './Guardian3D';
import ItemManager, { ITEMS_NOTE } from './ItemManager';

/** Port of the app's Character tab: Items (the Item Manager, in place of Equipment and Vault), 3D View, Inventory and Vendors. */
export default function CharacterTab({ sub }: { sub: string }) {
  if (sub === '3D View') return <Guardian3D />;
  return (
    <PrivateGate what={sub === 'Inventory' ? 'Lost items at the Postmaster, consumables, materials and mods.' : sub === 'Items' ? ITEMS_NOTE : 'What Zavala, the Gunsmith and the other vendors sell right now, and your rank with each.'} title={sub}>
      {sub === 'Inventory' ? <Inventory /> : sub === 'Items' ? <ItemManager /> : <Vendors />}
    </PrivateGate>
  );
}

/** The private pages need the Bungie sign-in of the Guardian shown. */
export function PrivateGate({ title, what, children }: { title: string; what: string; children: React.ReactNode }) {
  const h = useHub();
  const session = useSession();
  if (h.own) return <>{children}</>;
  return (
    <div className="g-signin g-card">
      <div className="g-signin-icon"><Art d={Draw.VaultDoor} size={22} /></div>
      <h3>{session.status === 'signedIn' ? `${title} is private` : 'Sign in with Bungie to see this'}</h3>
      <p className="g-text2">
        {what}{' '}
        {session.status === 'signedIn'
          ? 'Bungie only shows this to the Guardian themselves. You are looking up someone else.'
          : 'Bungie only shows this to a site you have signed in to with your Bungie account. You sign in on Bungie\'s own page; this site never sees your password.'}
      </p>
      <div className="g-landing-row">
        {session.status === 'signedIn' ? <button type="button" className="btn primary" onClick={h.clearLookup}>Show my own Guardian</button>
          : <button type="button" className="btn primary" onClick={session.signIn}>Sign in with Bungie</button>}
      </div>
    </div>
  );
}

const useGear = () => {
  const h = useHub();
  return usePrivate<GearChar[]>('gear', (t) => loadGear(h.player!, t || undefined), { public: true });
};
const useStash = () => {
  const h = useHub();
  return usePrivate<Stash>('stash', (t) => loadStash(h.player!, t));
};

// ---------------- moving and equipping ----------------

function useMover() {
  const h = useHub();
  const [note, setNote] = useState<string>();
  const [busy, setBusy] = useState(false);
  const run = async (what: string, item: StashItem, act: (token: string) => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    const verb = what.charAt(0).toLowerCase() + what.slice(1);
    setNote(`${item.name}: ${verb}...`);
    try {
      const t = await h.getToken();
      if (!t) throw new Error('Your Bungie sign-in has run out. Sign in again.');
      await act(t);
      setNote(`${item.name}: done (${verb}).`);
      h.touch();   // the stash and the gear have changed: read them again
    } catch (e) {
      setNote(`${item.name} stayed where it was: ${e instanceof Error ? e.message : e}`);
    } finally { setBusy(false); }
  };
  return { note, busy, run };
}

/** What can be done with an item from where it is: equip it, send it to the vault, send it to another character. */
const ui = () => Number(getComputedStyle(document.documentElement).getPropertyValue('--ui')) || 1;

function ItemMenu({ item, stash, at, close, run }: { item: StashItem; stash: Stash; at: { x: number; y: number }; close: () => void; run: ReturnType<typeof useMover>['run'] }) {
  const h = useHub();
  const nav = useNavigate();
  const holder = Object.entries(stash.characters).find(([, list]) => list.includes(item))?.[0] ?? null;   // null: in the vault
  const cls = (id: string) => h.hub?.chars.find((c) => c.id === id)?.cls ?? 'character';
  useEffect(() => {
    const off = () => close();
    window.addEventListener('click', off);
    window.addEventListener('scroll', off, true);
    return () => { window.removeEventListener('click', off); window.removeEventListener('scroll', off, true); };
  }, [close]);
  const p = h.player!;
  const option = (label: string, act: (t: string) => Promise<void>) => (
    <button key={label} type="button" role="menuitem" onClick={() => { close(); void run(label, item, act); }}>{label}</button>
  );
  return (
    // the page is scaled with the window (--ui): a position from the mouse is in screen pixels, the menu's in the page's
    <div className="g-menu floating" role="menu" style={{ left: Math.min(at.x, window.innerWidth - 240 * ui()) / ui(), top: Math.min(at.y, window.innerHeight - 220 * ui()) / ui() }} onClick={(e) => e.stopPropagation()}>
      <div className="g-menu-head">{item.name}</div>
      {holder && option(`Equip on this ${cls(holder)}`, (t) => equipItem(p, t, holder, item))}
      {holder && option('Move to the Vault', (t) => moveItem(p, t, item, holder, null))}
      {h.hub!.chars.filter((c) => c.id !== holder).map((c) => option(`Move to ${c.cls}`, (t) => moveItem(p, t, item, holder, c.id)))}
      <div className="g-menu-sep" />
      <button type="button" role="menuitem" onClick={() => { close(); nav(`/guardian/operations/d2-info?item=${item.hash}`); }}>Open in D2 Info</button>
    </div>
  );
}

function StashTile({ item, size = 52, onMenu }: { item: StashItem; size?: number; onMenu?: (item: StashItem, at: { x: number; y: number }) => void }) {
  const facts = [
    item.power !== undefined && `Power ${item.power}`, item.quantity > 1 && `${item.quantity.toLocaleString()} held`,
    isLocked(item) && 'Locked', isCrafted(item) && 'Shaped', isMasterwork(item) && 'Masterwork',
  ].filter(Boolean).join('  |  ');
  // a weapon or armour piece that is not waiting at the Postmaster can be moved and equipped from here
  const movable = (isWeapon(item) || isArmor(item)) && !!item.instanceId && item.bucket !== LOST_ITEMS && !!onMenu;
  // anything else opens in D2 Info (D2 Info finds any item now, not only weapons and armour)
  const gear = !movable;
  const nav = useNavigate();
  return (
    <ItemTile icon={item.icon} name={item.name} type={item.typeName} size={size} gold={isMasterwork(item)}
      corner={item.power !== undefined ? String(item.power) : item.quantity > 1 ? item.quantity.toLocaleString() : ''}
      more={facts + (movable ? '\n\nClick to move or equip it' : gear ? '\n\nClick to open it in D2 Info' : '')}
      onClick={movable ? (e) => { e.stopPropagation(); onMenu!(item, { x: e.clientX, y: e.clientY }); } : gear ? () => nav(`/guardian/operations/d2-info?item=${item.hash}`) : undefined} />
  );
}

// ---------------- Inventory ----------------

function Inventory() {
  const h = useHub();
  const { data: stash, error, again } = useStash();
  const { data: gear } = useGear();
  const mover = useMover();
  const [menu, setMenu] = useState<{ item: StashItem; at: { x: number; y: number } }>();
  const [pulling, setPulling] = useState(false);
  const [pullNote, setPullNote] = useState<string>();
  const char = h.char;
  if (!char) return <Message title="Inventory" text="No character to show." />;
  if (!stash) return <Message title="Inventory" text={error ?? 'Asking Bungie what you are carrying...'}>{error && <SmallButton onClick={again}>Try again</SmallButton>}</Message>;
  const onMenu = (item: StashItem, at: { x: number; y: number }) => setMenu({ item, at });
  const section = (title: string, list: StashItem[], cap: number, empty: string) => (
    <div className="g-invsection" key={title}>
      <SectionHead title={title.toUpperCase()} count={cap > 0 ? `${list.length} / ${cap}` : String(list.length)} warn={cap > 0 && list.length >= cap} />
      {list.length === 0 ? <p className="g-dim small">{empty}</p> : (
        <div className="g-wrap tiles">{[...list].sort((a, b) => b.tier - a.tier || a.name.localeCompare(b.name)).map((i, k) => <StashTile key={i.instanceId || `${i.hash}:${k}`} item={i} onMenu={onMenu} />)}</div>
      )}
    </div>
  );
  const lost = inBucket(stash, LOST_ITEMS, char.id);
  const bucketName = (b: number) => stash.buckets[b]?.name || (b === MATERIALS ? 'Materials' : 'Frontier Supplies');
  const groups = new Map<string, StashItem[]>();
  for (const i of stash.account) {
    if (i.bucket === VAULT || i.bucket === CONSUMABLES) continue;
    const k = bucketName(i.bucket);
    groups.set(k, [...(groups.get(k) ?? []), i]);
  }
  const ordered = [...groups.entries()].sort((a, b) => (a[0] === 'Materials' ? -1 : b[0] === 'Materials' ? 1 : a[0].localeCompare(b[0])));
  const worn = gear?.find((c) => c.id === char.id);
  const counted = worn?.items.filter((i) => (WEAPON_BUCKETS.includes(i.bucket) || ARMOR_BUCKETS.includes(i.bucket)) && i.power !== undefined) ?? [];
  // the account's currencies, then the upgrade materials people count the same way (they live among the consumables)
  const alsoMoney = ['Spoils of Conquest', 'Enhancement Core', 'Enhancement Prism', 'Ascendant Shard', 'Ascendant Alloy'];
  const purse = [...stash.currencies, ...alsoMoney.map((name) => inBucket(stash, CONSUMABLES).filter((i) => i.name === name))
    .filter((l) => l.length).map((l) => ({ name: l[0].name, icon: l[0].icon, quantity: l.reduce((s, i) => s + i.quantity, 0) }))]
    .filter((c, i, all) => all.findIndex((x) => x.name === c.name) === i);

  return (
    <div className="g-inv">
      {menu && <ItemMenu item={menu.item} stash={stash} at={menu.at} close={() => setMenu(undefined)} run={mover.run} />}
      <div className="g-stack">
        {section('Lost Items', lost, capacity(stash, LOST_ITEMS, 21), `Nothing is waiting at the Postmaster for your ${char.cls}.`)}
        {lost.length > 0 && (
          <div>
            <SmallButton disabled={pulling} title={`Moves these ${lost.length} lost item(s) from the Postmaster to your ${char.cls}, like the button in the game. Items that do not fit stay at the Postmaster.`}
              onClick={async () => {
                setPulling(true); setPullNote(undefined);
                let moved = 0; let refused: string | undefined;
                const t = await h.getToken();
                for (const item of lost) {
                  try { await pullFromPostmaster(h.player!, t!, char.id, item); moved++; } catch (e) { refused ??= `${item.name}: ${e instanceof Error ? e.message : e}`; }
                }
                setPulling(false);
                setPullNote(refused ? `Moved ${moved} of ${lost.length}. ${refused}` : `Moved ${moved} item(s) from the Postmaster.`);
                h.touch();
              }}>{pulling ? 'Pulling...' : 'Pull From Postmaster'}</SmallButton>
          </div>
        )}
        {pullNote && <p className="g-warn small">{pullNote}</p>}
        {mover.note && <p className="g-warn small">{mover.note}</p>}
        {section('Consumables', inBucket(stash, CONSUMABLES), capacity(stash, CONSUMABLES, 50), 'No consumables.')}
      </div>
      <div className="g-stack">
        {ordered.length ? ordered.map(([name, list]) => section(name, list, [...new Set(list.map((i) => i.bucket))].reduce((s, b) => s + capacity(stash, b, 0), 0), ''))
          : section('Modifications', [], capacity(stash, MODIFICATIONS, 50), 'No modifications.')}
        {counted.length > 0 && (
          <div className="g-invsection">
            <SectionHead title="POWER" />
            {counted.map((i) => (
              <div key={i.bucket} className="g-powerrow">
                <Pic src={i.icon} width={24} height={24} /><span>{i.name}</span><b className="gold">{i.power}</b>
              </div>
            ))}
            <div className="g-actual"><span>Actual Light</span><b className="gold">{Math.floor(counted.reduce((s, i) => s + (i.power ?? 0), 0) / counted.length)}</b></div>
          </div>
        )}
      </div>
      <div className="g-stack">
        {section('Engrams', inBucket(stash, ENGRAMS, char.id), capacity(stash, ENGRAMS, 10), `Your ${char.cls} is carrying no engrams.`)}
        <div className="g-invsection">
          <SectionHead title="CURRENCIES" />
          {purse.map((c) => (
            <div key={c.name} className="g-powerrow">
              <Pic src={c.icon} width={22} height={22} /><span>{c.name}</span><b>{c.quantity.toLocaleString()}</b>
            </div>
          ))}
        </div>
        <div><SmallButton onClick={again}>Read again</SmallButton></div>
        <p className="g-dim small">Lost items and engrams are your {char.cls}'s; the rest is shared by the account. Click your emblem in the header to switch character.</p>
      </div>
    </div>
  );
}

// ---------------- Vendors ----------------

export function Vendors({ tenets = false }: { tenets?: boolean }) {
  const h = useHub();
  const char = h.char;
  const { data: vendors, error, again } = usePrivate(char ? `vendors:${char.id}` : undefined, (t) => loadVendors(h.player!, t, char!.id));
  const [pick, setPick] = useState(0);
  if (!char) return <Message title="Vendors" text="No character to show." />;
  if (!vendors) return <Message title={tenets ? 'Tenets' : 'Vendors'} text={error ?? 'Asking Bungie what the vendors are selling...'}>{error && <SmallButton onClick={again}>Try again</SmallButton>}</Message>;
  const shops = vendors.filter((v) => isTenet(v) === tenets);
  if (!shops.length) return <Message title={tenets ? 'Tenets' : 'Vendors'} text={tenets ? 'Bungie listed no Tenets for this character.' : 'Bungie listed no vendors with anything on offer for this character.'} />;
  const open = shops.find((v) => v.hash === pick) ?? shops.find((v) => v.name.includes('Zavala')) ?? shops[0];
  const facts: string[] = [];
  if (open.rank !== undefined) facts.push(`Your rank: ${open.rank}`);
  if (open.refresh && open.refresh.getTime() > Date.now()) {
    const ms = open.refresh.getTime() - Date.now();
    facts.push(ms < 48 * 3_600_000 ? `New stock in ${Math.floor(ms / 3_600_000)} h ${Math.floor((ms % 3_600_000) / 60_000)} min` : `New stock ${open.refresh.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`);
  }
  return (
    <Split
      left={(
        <>
          <h1 className="g-title light">{tenets ? 'Tenets' : 'Vendors'}</h1>
          <p className="g-text2" style={{ marginBottom: 10 }}>{tenets ? `The Tenets and what each offers your ${char.cls}. Hover an item to see what it costs and whether you already have it.` : `What each vendor offers your ${char.cls} right now.`}</p>
          {shops.map((v) => <ListRow key={v.hash} name={v.name} count={String(saleCount(v))} selected={v === open} onClick={() => setPick(v.hash)} />)}
          <div style={{ marginTop: 6 }}><SmallButton onClick={again}>Read again</SmallButton></div>
        </>
      )}
      right={<VendorShelves v={open} facts={facts} />}
    />
  );
}

/** One vendor: picture, name and words, then each shelf of what they sell (weapons and armour open in D2 Info). */
export function VendorShelves({ v: open, facts }: { v: Vendor; facts: string[] }) {
  const nav = useNavigate();
  return (
    <>
      <div className="g-vendorhead">
        {open.icon && <Pic src={open.icon} width={64} height={64} />}
        <div>
          {open.subtitle && <div className="g-dim small">{open.subtitle}</div>}
          <div className="g-vendorname">{open.name}</div>
          {open.desc && <p className="g-text2">{open.desc}</p>}
          {facts.length > 0 && <div className="small">{facts.join('   |   ')}</div>}
        </div>
      </div>
      {open.categories.map((c) => (
        <div key={c.name} style={{ marginTop: 14 }}>
          <div className="g-text2">{c.name}</div>
          <div className="g-rule" />
          <div className="g-wrap tiles">
            {c.sales.map((s, k) => (
              <ItemTile key={`${s.hash}:${k}`} icon={s.icon} name={s.name} type={s.typeName} size={58} corner={s.quantity > 1 ? s.quantity.toLocaleString() : ''}
                more={`${s.cost ? 'Costs ' + s.cost : 'No cost'}${s.owned ? '\nYou already have this' : s.canBuy ? '' : '\nNot available to you right now'}\n\nClick to open it in D2 Info`} gold={s.owned} dim={!s.canBuy && !s.owned}
                onClick={() => nav(`/guardian/operations/d2-info?item=${s.hash}`)} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
